import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Calendar, Users, Trophy } from 'lucide-react';
import { StatsService } from '@/services/stats';
import { TournamentWithStats } from '@/types/stats';

const statsService = new StatsService();

export function TournamentHistory() {
  const [tournaments, setTournaments] = useState<TournamentWithStats[]>([]);
  const [loading, setLoading] = useState(true);

  const loadTournaments = useCallback(async () => {
    try {
      const data = await statsService.getTournaments();
      setTournaments(data);
    } catch (error) {
      console.error('Failed to load tournaments:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTournaments();
  }, [loadTournaments]);

  const getTournamentTypeLabel = (type: string) => {
    switch (type) {
      case 'mexicano': return 'Mexicano';
      case 'americano': return 'Americano';
      case 'team_americano': return 'Team Americano';
      default: return type;
    }
  };

  const getTournamentTypeBadge = (type: string) => {
    switch (type) {
      case 'mexicano': return 'default';
      case 'americano': return 'secondary';
      case 'team_americano': return 'outline';
      default: return 'outline';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Tournament History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">Loading tournaments...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Tournament History
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tournament</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-center">Players</TableHead>
              <TableHead className="text-center">Matches</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tournaments.map((tournament) => (
              <TableRow key={tournament.id}>
                <TableCell>
                  <div className="font-medium">{tournament.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {tournament.completed_rounds}/{tournament.total_rounds} rounds
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={getTournamentTypeBadge(tournament.tournament_type) as any}>
                    {getTournamentTypeLabel(tournament.tournament_type)}
                  </Badge>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Users className="h-4 w-4" />
                    {tournament.participants_count}
                  </div>
                </TableCell>
                <TableCell className="text-center">{tournament.matches_count}</TableCell>
                <TableCell className="text-center">
                  <Badge variant={tournament.is_completed ? 'default' : 'secondary'}>
                    {tournament.is_completed ? 'Completed' : 'In Progress'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    {new Date(tournament.created_at).toLocaleDateString()}
                  </div>
                  {tournament.completed_at && (
                    <div className="text-xs text-muted-foreground">
                      Completed: {new Date(tournament.completed_at).toLocaleDateString()}
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        
        {tournaments.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            No tournaments found. Start a tournament to see it appear here.
          </div>
        )}
      </CardContent>
    </Card>
  );
}